"use client";
import { ReactNode } from "react";
import { Elements, PaymentElement } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { FormProvider } from "react-hook-form";
import { Button, ButtonProps } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { FormInputField } from "@/components/form/FormInputField";
import { FormCheckboxBlock } from "@/components/form/FormCheckboxBlock";
import { Spinner } from "@/components/ui/Spinner";
import {
  useStripeCardSubmit,
  type StripeCardSubmitConsent,
} from "../hooks/useStripeCardSubmit";
import { twMerge } from "tailwind-merge";
import { InputProps } from "@/components/ui/Input";

export interface StripeCardFormInnerProps {
  onSuccess: (setupIntentId: string) => Promise<void>;
  onSubmitClick?: () => void;
  onError?: () => void;
  consent: StripeCardSubmitConsent;
  submitButtonProps?: Omit<ButtonProps, "onClick" | "loading" | "disabled">;
  cardholderInputProps?: Pick<InputProps, "placeholder">;
  footer?: ReactNode;
}

function StripeCardFormInner({
  onSuccess,
  onSubmitClick,
  onError,
  submitButtonProps,
  cardholderInputProps,
  footer,
  consent,
}: StripeCardFormInnerProps) {
  const {
    form,
    isReady,
    isSubmitting,
    isResolvingRedirect,
    stripeError,
    canSubmit,
    handleSubmit,
    handlePaymentElementReady,
    handlePaymentElementChange,
  } = useStripeCardSubmit({ consent, onSuccess, onSubmitClick, onError });

  const renderConsent = () => {
    switch (consent.type) {
      case "checkbox":
        return (
          <FormCheckboxBlock
            name="consent"
            disabled={isSubmitting}
            label="Patient consents to storing this card on file for future charges."
          />
        );
      case "text":
        return (
          <div className="text-content-secondary text-xs">{consent.text}</div>
        );
    }
  };

  if (isResolvingRedirect) {
    return (
      <div className="py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-5">
        <div>
          <PaymentElement
            onReady={handlePaymentElementReady}
            options={{
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
              terms: { card: "never" },
              wallets: {
                applePay: "never",
                googlePay: "never",
                link: "never",
              },
              fields: {
                billingDetails: {
                  address: "never",
                  name: "auto",
                },
              },
            }}
            onChange={handlePaymentElementChange}
          />
        </div>
        {isReady && (
          <>
            <FormInputField
              name="cardholderName"
              label="Cardholder Name"
              {...cardholderInputProps}
            />
            {renderConsent()}
            {stripeError && <Alert variant="error" message={stripeError} />}

            <Button
              onClick={handleSubmit}
              size="lg"
              disabled={!canSubmit}
              loading={isSubmitting}
              {...submitButtonProps}
              className={twMerge("flex-1", submitButtonProps?.className)}
            >
              Save Card
            </Button>
            {footer && <div className="-mt-1">{footer}</div>}
          </>
        )}
      </div>
    </FormProvider>
  );
}

interface StripeCardFormProps {
  stripePromise: Promise<Stripe | null>;
  clientSecret: string;
  onSuccess: StripeCardFormInnerProps["onSuccess"];
  onSubmitClick?: StripeCardFormInnerProps["onSubmitClick"];
  onError?: StripeCardFormInnerProps["onError"];
  consent: StripeCardFormInnerProps["consent"];
  submitButtonProps?: StripeCardFormInnerProps["submitButtonProps"];
  cardholderInputProps?: StripeCardFormInnerProps["cardholderInputProps"];
  footer?: StripeCardFormInnerProps["footer"];
}

export function StripeCardForm({
  stripePromise,
  clientSecret,
  onSuccess,
  onSubmitClick,
  onError,
  consent,
  submitButtonProps,
  cardholderInputProps,
  footer,
}: StripeCardFormProps) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          variables: {
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
            labelFontSize: "0.8125rem",
            fontSizeSm: "0.75rem",
            gridRowSpacing: "18px",
            labelSpacing: "8px",
            labelColorText: "#374151",
            labelFontWeight: "600",
            borderRadius: "12px",
            inputColorBorder: "#EBEDF2",
            inputBoxShadow: "none",
            colorDanger: "#E02800",
            inputFocusColorBorder: "#1884F9",
            focusBoxShadow: "none",
            inputFocusBoxShadow: "none",
          },
          rules: {
            ".Input": {
              padding: "16px",
              fontSize: "13px",
            },
            ".Input--invalid": {
              boxShadow: "none",
            },
            ".AccordionItem": {
              border: "none",
              boxShadow: "none",
            },
          },
        },
      }}
    >
      <StripeCardFormInner
        onSuccess={onSuccess}
        onSubmitClick={onSubmitClick}
        onError={onError}
        consent={consent}
        submitButtonProps={submitButtonProps}
        cardholderInputProps={cardholderInputProps}
        footer={footer}
      />
    </Elements>
  );
}
